import { Temporal } from 'temporal-polyfill';

import { PlainDateInitializer } from './plain-date-interval.js';
import { PlainDateTimeInterval } from './plain-datetime-interval.js';

export type PlainTimeInitializer = Temporal.PlainTime | Temporal.PlainTimeLike | string;

export class PlainTimeInterval {
  static from(start: PlainTimeInitializer, end: PlainTimeInitializer): PlainTimeInterval {
    return new this(Temporal.PlainTime.from(start), Temporal.PlainTime.from(end));
  }

  static compare(a: PlainTimeInterval, b: PlainTimeInterval): number {
    const startComparison = Temporal.PlainTime.compare(a.start, b.start);
    if (startComparison !== 0) {
      return startComparison;
    }
    return Temporal.PlainTime.compare(a.end, b.end);
  }

  constructor(
    public start: Temporal.PlainTime,
    public end: Temporal.PlainTime,
  ) {
    validatePlainTimeIntervalLike(start, end);
  }

  toString(): string {
    return `${this.start.toString()}/${this.end.toString()}`;
  }

  withPlainDate(plainDateLike: PlainDateInitializer): PlainDateTimeInterval {
    const plainDate = Temporal.PlainDate.from(plainDateLike);
    return PlainDateTimeInterval.from(this.start.toPlainDateTime(plainDate), this.end.toPlainDateTime(plainDate));
  }

  contains(time: Temporal.PlainTime): boolean {
    return Temporal.PlainTime.compare(this.start, time) <= 0 && Temporal.PlainTime.compare(time, this.end) < 0;
  }

  isOverlapping(other: PlainTimeInterval): boolean {
    if (this.start.equals(other.start) || this.end.equals(other.end)) {
      return true;
    }

    if (Temporal.PlainTime.compare(this.start, other.start) < 0) {
      return Temporal.PlainTime.compare(this.end, other.start) > 0;
    } else {
      return Temporal.PlainTime.compare(other.end, this.start) > 0;
    }
  }
}

export function validatePlainTimeIntervalLike(
  start: Temporal.PlainTime,
  end: Temporal.PlainTime,
  shouldRejectEqual = false,
): void {
  const comparison = Temporal.PlainTime.compare(start, end);

  if (shouldRejectEqual ? comparison >= 0 : comparison > 0) {
    throw new RangeError(
      shouldRejectEqual
        ? 'Start time must be strictly before end time'
        : 'Start time must be before or equal to end time',
    );
  }
}
